import React, { useEffect, useMemo, useState } from "react";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { useGetAllDepartments, useGetAllRoles } from "../hooks";

interface BusinessUserFilters {
  department: string[];
  role: string[];
  status: string;
}

interface BusinessUserFilterProps {
  department: string[];
  role: string[];
  status: string;
  onApplyFilters: (filters: BusinessUserFilters) => void;
  onResetFilters: () => void;
}

export const BusinessUserFilter: React.FC<BusinessUserFilterProps> = ({
  department,
  role,
  status,
  onApplyFilters,
  onResetFilters,
}) => {
  const [selectDepartment, setSelectDepartment] = useState<string[]>(department || []);
  
  const { data: departmentList } = useGetAllDepartments();
  const { data: roleList } = useGetAllRoles(selectDepartment);
  
  const departmentOptions = useMemo(
    () =>
      departmentList?.result?.map((item) => ({
        value: item.code,
        label: item.name,
      })) || [],
    [departmentList]
  );

  const roleOptions = useMemo(
    () =>
      roleList?.result?.flatMap(
        (item) =>
          item.roles?.map((role) => ({
            value: role.id,
            label: role.roleName,
          })) || []
      ) || [],
    [roleList]
  );
  
  const filterFields: FormField[] = useMemo(
    () => [
      {
        name: "department",
        label: "Department",
        type: "multi-select",
        placeholder: "Select Department(s)",
        options: departmentOptions,
        className: "col-span-1",
        classes: {
          container: "w-full mb-6",
        },
      },
      {
        name: "role",
        label: "Role",
        type: "multi-select",
        placeholder: "Select Role(s)",
        options: roleOptions,
        className: "col-span-1",
        classes: {
          container: "w-full mb-6",
        },
      },
      {
        name: "status",
        label: "Status",
        type: "select",
        placeholder: "Select Status",
        options: [
          { value: "active", label: "Active" },
          { value: "inactive", label: "Inactive" },
          { value: "pending", label: "Pending" },
        ],
        className: "col-span-1",
        classes: {
          container: "w-full mb-6",
        },
      },
    ],
    [departmentOptions, roleOptions]
  );

  const form = FormService.createForm({
    fields: filterFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: {
      department: department || [],
      role: role || [],
      status: status || "",
    },
  });

  // Update form when filter props change (for auto-population in edit mode)
  useEffect(() => {
    form.reset({
      department: department || [],
      role: role || [],
      status: status || "",
    });
    
    // Update selectDepartment to fetch roles when department is auto-populated
    if (department && department.length > 0) {
      setSelectDepartment(department);
    }
  }, [department, role, status, form]);

  const handleFilterSubmit = () => {
    const formValues = form.getValues();

    onApplyFilters({
      department: formValues.department || [],
      role: formValues.role || [],
      status: formValues.status || "",
    });
  };

  const handleFilterReset = () => {
    form.reset({
      department: [],
      role: [],
      status: "",
    });
    
    setSelectDepartment([]);
    onResetFilters();
  };

  const handleFieldChange = (field: string, value: any) => {
    switch (field) {
      case "department":
        setSelectDepartment(Array.isArray(value) ? value : [value]);
        // Reset role when department changes
        form.setValue("role", []);
        break;
    }
    console.log(`Filter field ${field} changed to`, value);
  };

  return (
    <div>
      <DynamicForm
        fields={filterFields}
        form={form}
        onSubmit={handleFilterSubmit}
        onReset={handleFilterReset}
        onFieldChange={handleFieldChange}
        gridColumns={2}
        config={{
          layout: "vertical",
          showSubmitButton: true,
          showResetButton: true,
          submitButtonText: "Apply Filters",
          resetButtonText: "Clear",
          classes: {
            buttonContainer: "flex justify-end gap-4",
          },
        }}
      />
    </div>
  );
};